// Gmail-Dienst: OAuth 2.0 PKCE + gmail.send + gmail.readonly (Firmen-Akte)
// Nutzt dieselbe Google-OAuth-App wie Drive (gleiche Client-ID/Secret),
// aber separaten Scope + eigene localStorage-Keys.
// Hinweis: Bestehende Verbindungen (nur send-Scope) müssen für den
// E-Mail-Import einmal neu verbunden werden (Scope-Erweiterung).

import { loadAussteller } from './settingsService';

const GMAIL_SCOPE   = 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly';
const REDIRECT_URI  = (): string => window.location.origin + '/auth/google/callback';

const LS_GMAIL_REFRESH  = 'amed_gmail_refresh';
const LS_GMAIL_ACCESS   = 'amed_gmail_access';
const LS_GMAIL_ACCESS_EXP = 'amed_gmail_access_exp';
const LS_GMAIL_CLIENT_ID  = 'amed_gmail_clientid';
const LS_GMAIL_CLIENT_SEC = 'amed_gmail_clientsecret';

// ── PKCE ──────────────────────────────────────────────────────────────────

function base64url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateVerifier(): string {
  const arr = new Uint8Array(32); crypto.getRandomValues(arr); return base64url(arr.buffer);
}

async function generateChallenge(v: string): Promise<string> {
  return base64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v)));
}

// ── OAuth-Flow ────────────────────────────────────────────────────────────

export async function startGmailOAuth(clientId: string, clientSecret: string): Promise<void> {
  const verifier  = generateVerifier();
  const challenge = await generateChallenge(verifier);
  const state     = base64url(crypto.getRandomValues(new Uint8Array(16)).buffer);

  localStorage.setItem(LS_GMAIL_CLIENT_ID,  clientId);
  localStorage.setItem(LS_GMAIL_CLIENT_SEC, clientSecret);
  localStorage.setItem('amed_gmail_verifier', verifier);
  localStorage.setItem('amed_gmail_state',    state);
  localStorage.setItem('amed_gmail_pending',  'true');

  const params = new URLSearchParams({
    response_type: 'code', client_id: clientId,
    redirect_uri:  REDIRECT_URI(), scope: GMAIL_SCOPE,
    access_type: 'offline', prompt: 'consent',
    state, code_challenge: challenge, code_challenge_method: 'S256',
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export async function handleGmailOAuthCallback(code: string, state: string): Promise<boolean> {
  const savedState   = localStorage.getItem('amed_gmail_state');
  const codeVerifier = localStorage.getItem('amed_gmail_verifier');
  const clientId     = localStorage.getItem(LS_GMAIL_CLIENT_ID);
  const clientSecret = localStorage.getItem(LS_GMAIL_CLIENT_SEC);

  localStorage.removeItem('amed_gmail_state');
  localStorage.removeItem('amed_gmail_verifier');
  localStorage.removeItem('amed_gmail_pending');

  if (state !== savedState || !codeVerifier || !clientId || !clientSecret) return false;

  try {
    const resp = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        code, client_id: clientId, client_secret: clientSecret,
        redirect_uri: REDIRECT_URI(), grant_type: 'authorization_code',
        code_verifier: codeVerifier,
      }),
    });
    if (!resp.ok) return false;
    const data = await resp.json() as { access_token: string; refresh_token?: string; expires_in: number };
    localStorage.setItem(LS_GMAIL_ACCESS,     data.access_token);
    localStorage.setItem(LS_GMAIL_ACCESS_EXP, String(Date.now() + data.expires_in * 1000));
    if (data.refresh_token) localStorage.setItem(LS_GMAIL_REFRESH, data.refresh_token);
    return true;
  } catch { return false; }
}

// ── Status ────────────────────────────────────────────────────────────────

export function isGmailConnected(): boolean {
  return localStorage.getItem(LS_GMAIL_REFRESH) !== null;
}

export function disconnectGmail(): void {
  [LS_GMAIL_REFRESH, LS_GMAIL_ACCESS, LS_GMAIL_ACCESS_EXP,
   LS_GMAIL_CLIENT_ID, LS_GMAIL_CLIENT_SEC].forEach(k => localStorage.removeItem(k));
}

// ── Token ─────────────────────────────────────────────────────────────────

async function getGmailToken(): Promise<string | null> {
  const token = localStorage.getItem(LS_GMAIL_ACCESS);
  const exp   = Number(localStorage.getItem(LS_GMAIL_ACCESS_EXP) ?? '0');
  if (token && exp > Date.now() + 60_000) return token;

  const refresh = localStorage.getItem(LS_GMAIL_REFRESH);
  const id      = localStorage.getItem(LS_GMAIL_CLIENT_ID);
  const sec     = localStorage.getItem(LS_GMAIL_CLIENT_SEC);
  if (!refresh || !id || !sec) return null;

  const resp = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ refresh_token: refresh, client_id: id, client_secret: sec, grant_type: 'refresh_token' }),
  });
  if (!resp.ok) return null;
  const data = await resp.json() as { access_token: string; expires_in: number };
  localStorage.setItem(LS_GMAIL_ACCESS,     data.access_token);
  localStorage.setItem(LS_GMAIL_ACCESS_EXP, String(Date.now() + data.expires_in * 1000));
  return data.access_token;
}

// ── Senden ────────────────────────────────────────────────────────────────

function toBase64Url(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export async function sendEmailViaGmail(opts: {
  to:      string;
  subject: string;
  body:    string;
}): Promise<void> {
  let token: string | null;
  try {
    token = await getGmailToken();
  } catch (e) {
    throw new Error(`[Token-Fetch] ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!token) throw new Error('Gmail nicht verbunden — bitte neu verbinden.');

  const aussteller = await loadAussteller();
  const fromName   = `${aussteller.titelVor} ${aussteller.name}`.trim();
  const fromAddr   = aussteller.email || token;
  const fromHeader = fromName ? `${fromName} <${fromAddr}>` : fromAddr;

  const message = [
    `From: ${fromHeader}`,
    `To: ${opts.to}`,
    `Subject: ${opts.subject}`,
    'Content-Type: text/plain; charset=UTF-8',
    '',
    opts.body,
  ].join('\r\n');

  const encoded = toBase64Url(message);

  let resp: Response;
  try {
    resp = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ raw: encoded }),
    });
  } catch (e) {
    throw new Error(`[API-Netzwerk] ${e instanceof Error ? e.message : String(e)}`);
  }

  if (!resp.ok) {
    const errText = await resp.text().catch(() => '');
    throw new Error(`[API-${resp.status}] ${errText.slice(0, 300)}`);
  }
}

// ── Posteingang lesen (Firmen-Akte) ───────────────────────────────────────

export interface InboxMessage {
  id:      string;
  from:    string;   // reine Adresse, z.B. martin@ramsauer.at
  subject: string;
  date:    string;   // ISO
  body:    string;   // text/plain (bzw. Snippet als Fallback)
}

function decodeBase64Url(data: string): string {
  const b64 = data.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

interface GmailPart {
  mimeType?: string;
  body?: { data?: string };
  parts?: GmailPart[];
}

function extractPlainText(part: GmailPart): string | null {
  if (part.mimeType === 'text/plain' && part.body?.data !== undefined) {
    return decodeBase64Url(part.body.data);
  }
  for (const p of part.parts ?? []) {
    const found = extractPlainText(p);
    if (found !== null) return found;
  }
  return null;
}

/** Extrahiert die reine Adresse aus einem From-Header („Name <a@b.at>" → a@b.at). */
export function extractAddress(fromHeader: string): string {
  const m = /<([^>]+)>/.exec(fromHeader);
  return (m?.[1] ?? fromHeader).trim().toLowerCase();
}

/**
 * Liest die letzten Posteingang-Nachrichten (Standard: 30 Tage, max. 50).
 * Benötigt gmail.readonly — ältere Verbindungen einmal neu verbinden.
 */
export async function listInboxMessages(days = 30, max = 50): Promise<InboxMessage[]> {
  const token = await getGmailToken();
  if (!token) throw new Error('Gmail nicht verbunden — bitte neu verbinden.');
  const auth = { Authorization: `Bearer ${token}` };

  const q = encodeURIComponent(`in:inbox newer_than:${days}d`);
  const listResp = await fetch(
    `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${q}&maxResults=${max}`,
    { headers: auth },
  );
  if (listResp.status === 403) {
    throw new Error('Lese-Berechtigung fehlt — bitte Gmail einmal neu verbinden (Scope-Erweiterung).');
  }
  if (!listResp.ok) throw new Error(`Gmail-Liste fehlgeschlagen (${listResp.status})`);
  const listData = await listResp.json() as { messages?: Array<{ id: string }> };
  const ids = (listData.messages ?? []).map(m => m.id);

  const result: InboxMessage[] = [];
  for (const id of ids) {
    const msgResp = await fetch(
      `https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}?format=full`,
      { headers: auth },
    );
    if (!msgResp.ok) continue;
    const msg = await msgResp.json() as {
      id: string;
      snippet?: string;
      internalDate?: string;
      payload?: GmailPart & { headers?: Array<{ name: string; value: string }> };
    };
    const headers = msg.payload?.headers ?? [];
    const header = (name: string): string =>
      headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value ?? '';

    const body = (msg.payload !== undefined ? extractPlainText(msg.payload) : null)
      ?? msg.snippet ?? '';

    result.push({
      id: msg.id,
      from: extractAddress(header('From')),
      subject: header('Subject') || '(kein Betreff)',
      date: new Date(Number(msg.internalDate ?? Date.now())).toISOString(),
      body: body.slice(0, 20_000),
    });
  }
  return result;
}
